import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QrCode, Edit, Trash2, TrendingUp, User, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StoreTable } from "@/models/tableModel";
import { useState } from "react";
import { StaffAssignmentDialog } from "./StaffAssignmentDialog";
import { useTranslation } from "react-i18next";
import { EditTableDialog } from "./EditTableDialog";

interface TableCardProps {
  table: StoreTable;
  onViewQR: () => void;
  onEdit: (tableData: { name: string; number: string }) => void;
  onDelete: () => void;
}

export function TableCard({
  table,
  onViewQR,
  onEdit,
  onDelete,
}: TableCardProps) {
  const { t } = useTranslation();
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const handleEditSuccess = async (tableData: {
    name: string;
    number: string;
  }) => {
    onEdit(tableData);
    setShowEditDialog(false);
  };

  return (
    <>
      <Card className="relative overflow-hidden transition-all duration-300 hover:shadow-xl bg-gradient-to-br from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl -z-10" />

        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-xl tracking-tight">
                  {table.name}
                </h3>
                <Badge
                  variant="secondary"
                  className="text-xs font-medium bg-secondary/30 backdrop-blur-sm"
                >
                  {t("tableCard.scansToday", { count: table.daily_scans })}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("tableCard.number", { number: table.number })}
              </p>
            </div>

            {table.total_scans > 0 && (
              <div className="flex items-center text-sm text-muted-foreground bg-muted/30 px-3 py-1.5 rounded-full">
                <TrendingUp className="w-4 h-4 mr-2 text-primary" />
                {t("tableCard.totalScans", { count: table.total_scans })}
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Staff Assignment Info */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-gradient-to-br from-primary/5 to-secondary/5 dark:from-primary/10 dark:to-secondary/10 rounded-xl backdrop-blur-sm border border-slate-200/50 dark:border-slate-700/50 shadow-sm">
            <div className="flex items-center text-sm">
              <div className="p-2.5 bg-primary/15 dark:bg-primary/20 rounded-full mr-3 shadow-inner">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="font-medium text-base">
                  {table.staffName
                    ? table.staffName
                    : t("tableCard.noStaffAssigned")}
                </span>
                {table.staffName && (
                  <span className="text-sm text-muted-foreground">
                    ({t("tableCard.assigned")})
                  </span>
                )}
              </div>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowStaffDialog(true)}
              className="w-full sm:w-auto bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 shadow-sm"
            >
              {table.assigned_staff_id
                ? t("tableCard.change")
                : t("tableCard.assign")}
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
            <Button
              size="sm"
              variant="default"
              onClick={onViewQR}
              className="col-span-2 sm:flex-1 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary"
            >
              <QrCode className="w-4 h-4 mr-2" />
              {t("tableCard.qrCode")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowEditDialog(true)}
              className="hover:bg-muted/50"
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onDelete}
              className="text-destructive hover:text-destructive-foreground hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Scan Activity Indicator */}
          {/* <div className="relative w-full">
            <div className="w-full bg-secondary/20 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-500 ease-out"
                style={{
                  width: `${Math.min((table.daily_scans / 10) * 100, 100)}%`,
                }}
              />
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="absolute -right-2 -top-1 h-4 w-4 p-0 hover:bg-transparent"
                  >
                    <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {t("tableCard.progressInfo", {
                      current: table.daily_scans,
                      target: 10,
                      defaultValue:
                        "Shows daily scan progress ({{current}}/{{target}} scans)",
                    })}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div> */}
        </CardContent>
      </Card>

      <StaffAssignmentDialog
        open={showStaffDialog}
        onOpenChange={setShowStaffDialog}
        table={table}
      />

      <EditTableDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        table={table}
        onSave={handleEditSuccess}
      />
    </>
  );
}
